import Editor, { useMonaco } from "@monaco-editor/react";
import React, { useEffect, useState } from "react";

import { useTheme } from "../../../../context/ThemeContext";
import Header from "./WebHeader";
import Output from "./WebOutput";

const editorOptions = {
  minimap: { enabled: false },
  fontSize: 14,
  wordWrap: "off",
  lineNumbers: "on",
  scrollBeyondLastLine: false,
  smoothScrolling: true,
  renderLineHighlight: "all",
  tabSize: 2,
  formatOnType: true,
  automaticLayout: true,
  cursorStyle: "line",
  folding: true,
  suggestOnTriggerCharacters: true,
  quickSuggestions: { other: true, comments: true, strings: true },
  bracketPairColorization: { enabled: true },
  codeLens: true,
  links: true,
  matchBrackets: "always",
};

const CodeEditor = ({
  isTeacher = false,
  initialCode,
  html,
  css,
  js,
  setHtml,
  setCss,
  setJs,
  resetWebCode = () => {},
  hasCodeContent = () => {},
  reRunCode = false,
}) => {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState("html");
  const [isHorizontal, setIsHorizontal] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [output, setOutput] = useState(null);
  const monaco = useMonaco();

  useEffect(() => {
    if (monaco) {
      const lightTheme = {
        base: "vs",
        inherit: true,
        rules: [],
        colors: {
          "editor.background": "#ffffff",
          "editor.lineHighlightBackground": "#f0f0f0",
        },
      };

      const darkTheme = {
        base: "vs-dark",
        inherit: true,
        rules: [],
        colors: {
          "editor.background": "#1e272e",
          "editor.lineHighlightBackground": "#2a2d2e",
        },
      };

      monaco?.editor?.defineTheme("custom-light", lightTheme);
      monaco?.editor?.defineTheme("custom-dark", darkTheme);
    }
    monaco?.editor?.setTheme(theme === "dark" ? "custom-dark" : "custom-light");
  }, [monaco]);

  useEffect(() => {
    setHtml(initialCode?.html || "");
    setCss(initialCode?.css || "");
    setJs(initialCode?.js || "");
    // Call onRunCode with the current values after setting the state
    onRunCode(initialCode?.html, initialCode?.css, initialCode?.js);
  }, [initialCode?.js, initialCode?.css, initialCode?.html, reRunCode]);

  const onRunCode = (htmlParam, cssParam, jsParam) => {
    setIsLoading(true);

    const combinedOutput = `
      <html>
        <head>
          <style>${cssParam || css}</style>
        </head>
        <body>
          ${htmlParam || html}
          <script>${jsParam || js}<\/script>
        </body>
      </html>
    `;

    setOutput(combinedOutput);
    setIsLoading(false);
  };

  const resetWebCodeAndRunCode = () => {
    resetWebCode();
    onRunCode("");
    setOutput("");
  };

  return (
    <div className="ltr-ignore">
      <Header
        isTeacher={isTeacher}
        onRunCode={onRunCode}
        isLoading={isLoading}
        setIsHorizontal={setIsHorizontal}
        isHorizontal={isHorizontal}
        resetWebCodeAndRunCode={resetWebCodeAndRunCode}
        hasCodeContent={hasCodeContent}
      />
      <div className={`flex ${isHorizontal ? "gap-2" : "flex-col space-y-2"}`}>
        <div
          className={`w-full rounded-lg border border-gray-50 bg-white p-0 pb-4 shadow-md dark:border-dark-border dark:bg-[#1e272e] ${isHorizontal ? "lg:w-1/2" : ""}`}
        >
          <div className="mb-4 flex justify-between">
            <div>
              {["html", "css", "javascript"].map((tab, index) => (
                <button
                  key={tab}
                  className={`px-4 py-2 text-sm font-medium transition-colors duration-300 ${activeTab === tab ? "bg-blue-500 text-white" : "bg-gray-50 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-100"} ${index === 0 ? "rounded-tl-lg" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {["html", "css", "javascript"].map((tab) => (
            <div key={tab} style={{ display: activeTab === tab ? "block" : "none" }}>
              <Editor
                theme={theme === "dark" ? "custom-dark" : "custom-light"}
                options={{ ...editorOptions }}
                language={tab}
                height={isHorizontal ? "52vh" : "22vh"}
                value={tab === "html" ? html : tab === "css" ? css : js}
                onChange={(value) =>
                  tab === "html" ? setHtml(value) : tab === "css" ? setCss(value) : setJs(value)
                }
              />
            </div>
          ))}
        </div>

        <Output output={output} isHorizontal={isHorizontal} />
      </div>
    </div>
  );
};

export default CodeEditor;
